import * as datefns from "date-fns";
import {ApiDate, ApiDateTime, nominate} from "platformed-client/types";
import {DateInput} from "platformed-components/i18n/dateUtils";

type CopyNullability<A, B> = null extends A ? B | null : B;
type CopyUndefinedness<A, B> = undefined extends A ? B | undefined : B;

export function parseISO<T extends ApiDateTime | ApiDate | null | undefined>(
  date: T,
): CopyNullability<T, CopyUndefinedness<T, Date>>;
export function parseISO(date: ApiDateTime | ApiDate | null | undefined): Date | null | undefined {
  if (date == null) {
    return date;
  } else {
    return datefns.parseISO(date);
  }
}

export function formatISO<T extends Date | null | undefined>(
  date: T,
): CopyNullability<T, CopyUndefinedness<T, ApiDateTime>>;
export function formatISO(date: Date | null | undefined): ApiDateTime | null | undefined {
  if (date == null) {
    return date;
  } else {
    return nominate("datetime", date.toISOString());
  }
}

/**
 * Parse an `ApiDate` into a JS `Date`.
 *
 * The date is interpreted as midnight in the local timezone.
 *
 * Use this for dates that are not considered to change with timezone.
 * For example, a birthday, or the date of incorporation of a company.
 */
export function parseNaiveDate<T extends ApiDateTime | ApiDate | null | undefined>(
  date: T,
): CopyNullability<T, CopyUndefinedness<T, Date>>;
export function parseNaiveDate(date: ApiDateTime | ApiDate | null | undefined): Date | null | undefined {
  if (date == null) {
    return date;
  }

  const [year, month, day] = date.split("-");
  const parsedDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));

  if (Number.isNaN(parsedDate.valueOf())) {
    throw new Error("Failed to parse date");
  }

  return parsedDate;
}

/**
 * Convert a JS `Date` into an naive `ApiDate`.
 *
 * The time and timezone portion of the date are ignored.
 *
 * Use this for dates that are not considered to change with timezone.
 * For example, a birthday, or the date of incorporation of a company.
 */
export function formatNaiveDate<T extends Date | null | undefined>(
  date: T,
): CopyNullability<T, CopyUndefinedness<T, ApiDate>>;
export function formatNaiveDate(date: Date | null | undefined): ApiDate | null | undefined {
  if (date == null) {
    return date;
  }

  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");

  const formattedDate = `${year}-${month}-${day}`;
  return nominate("date", formattedDate);
}

export function compareApiDateTimes(a: ApiDateTime, b: ApiDateTime): number {
  return parseISO(a).getTime() - parseISO(b).getTime();
}

/** Current time as an ApiDateTime. */
export function now(): ApiDateTime {
  return nominate("datetime", new Date().toISOString());
}

/** Current date (naive, local timezone) as an ApiDate. */
export function today(): ApiDate {
  const d = new Date();
  const year = d.getFullYear();
  const month = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  return nominate("date", `${year}-${month}-${day}`);
}

export function isSameMonth(a: DateInput, b: DateInput): boolean {
  return datefns.isSameMonth(datefns.parseISO(a), datefns.parseISO(b));
}

export function isSameYear(a: DateInput, b: DateInput): boolean {
  return datefns.isSameYear(datefns.parseISO(a), datefns.parseISO(b));
}

/** Returns a - b in calendar days. */
export function differenceInCalendarDays(a: DateInput, b: DateInput): number {
  return datefns.differenceInCalendarDays(datefns.parseISO(a), datefns.parseISO(b));
}

export function addDays(date: DateInput, days: number): ApiDateTime {
  const result = datefns.add(datefns.parseISO(date), {days});
  return nominate("datetime", result.toISOString());
}

/** Whether the given date is more than one year ago. */
export function isOlderThanOneYear(date: DateInput): boolean {
  return datefns.differenceInYears(new Date(), datefns.parseISO(date)) >= 1;
}

/** Milliseconds since epoch. */
export function toTimestamp(date: DateInput): number {
  return datefns.parseISO(date).getTime();
}

/** Signed month difference (a - b). */
export function differenceInMonths(a: DateInput, b: DateInput): number {
  const da = datefns.parseISO(a);
  const db = datefns.parseISO(b);
  return (da.getFullYear() - db.getFullYear()) * 12 + (da.getMonth() - db.getMonth());
}

/** Full calendar months in the inclusive range [start, end]. */
export function fullMonthsBetween(start: ApiDate, end: ApiDate): number {
  const s = datefns.parseISO(start);
  const e = datefns.parseISO(end);
  if (e < s) return 0;

  const eAdj = new Date(e);
  eAdj.setDate(eAdj.getDate() + 1);

  let months = (eAdj.getFullYear() - s.getFullYear()) * 12 + (eAdj.getMonth() - s.getMonth());
  if (eAdj.getDate() < s.getDate()) months--;

  return months;
}
